/**
 * ตัวจัดการเส้นทางในหน้าเว็บ — ทำเองแทนการเพิ่ม react-router
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องมี
 * ────────────────────────────────────────────────────────────────
 *
 * เดิมทั้งแอปจำหน้าปัจจุบันไว้ใน useState ตัวเดียว ซึ่งพอมี dashboard ที่ต้อง
 * ไล่ลงชั้น กระทรวง → สำนัก → ส่วน → รายบุคคล แล้วมันไม่พออีกต่อไป
 *
 *   · กดปุ่มย้อนกลับของเบราว์เซอร์แล้วหลุดออกจากแอปทั้งที่ผู้ใช้แค่อยากถอยขึ้นหนึ่งชั้น
 *   · ส่งลิงก์หน้าของคนหนึ่งคนให้เพื่อนดูไม่ได้ ต้องบอกให้เขาไล่กดเองใหม่
 *   · กด refresh แล้วเด้งกลับหน้าแรกทุกครั้ง
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมใช้ hash ไม่ใช่ History API
 * ────────────────────────────────────────────────────────────────
 *
 * เส้นทางแบบ /org/xxx ต้องให้เซิร์ฟเวอร์ตอบ index.html กลับมาทุก path
 * ซึ่งฝั่งเซิร์ฟเวอร์ทำไว้แล้ว (http/clientAssets.ts) แต่การใช้ hash ทำให้
 * ระบบนี้ยังทำงานถูกแม้วันหนึ่งถูกเอาไปวางไว้ใต้ path ย่อยของโดเมนอื่น
 * เช่น https://xxx.go.th/yana/ โดยไม่ต้องตั้ง base path ใหม่ทั้งระบบ
 *
 * ────────────────────────────────────────────────────────────────
 *  ข้อควรระวังเรื่อง id ที่มีอักขระพิเศษ
 * ────────────────────────────────────────────────────────────────
 *
 * id ของหน่วยงานคือเส้นทางเต็ม เช่น 'สป.กค. > สำนักบริหารกลาง' ซึ่งมีทั้งช่องว่าง
 * และเครื่องหมาย > จึงต้อง encode ก่อนใส่ลง hash เสมอ และ decode ตอนอ่านออกมา
 * ถ้าลืม จะได้หน่วยงานที่หาไม่เจอโดยไม่มีข้อความบอกว่าเพราะอะไร
 */
import { useEffect, useState } from 'react';

export type Route =
  | { name: 'dashboard' }
  | { name: 'career' }
  | { name: 'people' }
  | { name: 'flow' }
  | { name: 'authority' }
  | { name: 'org' }
  | { name: 'ask' }
  | { name: 'unit'; unitId: string }
  | { name: 'person'; personaId: string };

/** หน้าที่เปิดขึ้นมาเมื่อ hash ว่างหรืออ่านไม่ออก */
const HOME: Route = { name: 'dashboard' };

const SIMPLE = new Set(['dashboard', 'career', 'people', 'flow', 'authority', 'org', 'ask']);

export function parseHash(hash: string): Route {
  const raw = hash.replace(/^#\/?/, '');
  if (!raw) return HOME;

  const [head, ...rest] = raw.split('/');
  const arg = rest.length > 0 ? safeDecode(rest.join('/')) : '';

  if (head === 'unit' && arg) return { name: 'unit', unitId: arg };
  if (head === 'person' && arg) return { name: 'person', personaId: arg };
  if (head && SIMPLE.has(head)) return { name: head } as Route;

  return HOME;
}

export function toHash(route: Route): string {
  switch (route.name) {
    case 'unit':
      return `#/unit/${encodeURIComponent(route.unitId)}`;
    case 'person':
      return `#/person/${encodeURIComponent(route.personaId)}`;
    default:
      return `#/${route.name}`;
  }
}

/**
 * เปลี่ยนหน้า
 *
 * `replace` ใช้ตอนที่ไม่อยากให้หน้าเดิมค้างอยู่ในประวัติ เช่นตอนแก้ hash ที่พิมพ์ผิด
 * ให้เป็นหน้าแรก — ไม่งั้นกดย้อนกลับแล้วจะวนกลับไป hash ที่ผิดอันเดิมไม่รู้จบ
 */
export function navigate(route: Route, replace = false): void {
  const next = toHash(route);
  if (window.location.hash === next) return;
  if (replace) window.history.replaceState(null, '', next);
  else window.location.hash = next;
  // replaceState ไม่ยิง hashchange เอง จึงต้องบอกให้ useRoute รู้ด้วยตัวเอง
  if (replace) window.dispatchEvent(new HashChangeEvent('hashchange'));
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));

  useEffect(() => {
    const onChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  return route;
}

/**
 * decodeURIComponent โยน error ได้ถ้า hash มี % ที่ไม่ใช่รหัส escape ที่ถูกต้อง
 * ซึ่งเกิดได้ง่ายเวลาคนคัดลอกลิงก์แล้วตัดตัวอักษรท้ายหายไป
 * กรณีนั้นให้ใช้ข้อความดิบไปก่อน แล้วปล่อยให้หน้าจอขึ้นว่าไม่พบหน่วยงาน
 * ดีกว่าทำให้ทั้งแอปขาวทั้งหน้า
 */
function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
